import { router, useLocalSearchParams } from 'expo-router';
import { useAtomValue, useSetAtom } from 'jotai';
import { useState } from 'react';
import { Keyboard, Pressable } from 'react-native';
import { type SceneRendererProps, TabView } from 'react-native-tab-view';
import {
    DEFAULT_COLOR,
    DEFAULT_ICON,
    FullScreenSpinner,
    InfoButton,
    isWeb,
    ScreenContainer,
    useToastController,
} from 'smoothie';

import { isEmpty } from '../../../../common/misc';
import LogRocketFacade from '../../../../logger/log-rocket/LogRocketFacade';
import { captureException } from '../../../../logger/Logger';
import useGetAllBankAccountDetails from '../../../hooks/useGetAllBankAccountDetails';
import useUpdateBankAccount from '../../../hooks/useUpdateBankAccount';
import {
    editingSpaceAtom,
    preselectedAccountForCardAtom,
} from '../../../state/SpacesAtoms';
import {
    type AccountDetail,
    HolisticCategory,
    type UpdateBankAccountInfo,
} from '../../../types/BankAccount';
import Color from './steps/Color';
import Customize from './steps/Customize';
import Icon from './steps/Icon';
import Name from './steps/Name';
import Spend, { SpendInfoSheet, SpendType } from './steps/Spend';

enum CreateEditSteps {
    CUSTOMIZE = 'customize',
    SPEND = 'spend',
    NAME = 'name',
    ICON = 'icon',
    COLOR = 'color',
}

const CREATE_EDIT_TITLE = {
    name: 'Name',
    icon: 'Choose an icon',
    color: 'Choose a color',
};

interface AccountUpdateDetails {
    bankAccountId: string;
    syncteraAccountId: string;
    nickname: string;
    icon: string;
    color: string;
    holisticCategory: HolisticCategory;
}

const convertSpendTypeToHolisticCategory = (
    spendType: SpendType,
): HolisticCategory => {
    switch (spendType) {
        case SpendType.SAVE:
            return HolisticCategory.SAVE;
        case SpendType.SPEND:
            return HolisticCategory.SPEND;
    }
};

const CustomizeSpace = () => {
    LogRocketFacade.tagPage('CreateEditPage');

    const toast = useToastController();

    const { fromKyc } = useLocalSearchParams();

    const setEditingSpaceAccount = useSetAtom(editingSpaceAtom);
    const setPreselectedAccountFromCard = useSetAtom(
        preselectedAccountForCardAtom,
    );

    const account = useAtomValue(preselectedAccountForCardAtom);

    const [index, setIndex] = useState(0);
    const [isInfoSheetOpen, setIsInfoSheetOpen] = useState(false);

    const [updateAccountInfo, setUpdateAccountInfo] =
        useState<UpdateBankAccountInfo>({
            nickname: account?.nickname ?? undefined,
            icon: account?.icon ?? DEFAULT_ICON,
            color: account?.color ?? DEFAULT_COLOR,
            holisticCategory:
                account?.holisticCategory ?? HolisticCategory.SPEND,
        });

    const [spendType, setSpendType] = useState<SpendType | null>(null);

    const { refetchAccountDetails } = useGetAllBankAccountDetails();

    const { mutateAsync: updateAccount, isPending: isUpdateAccountPending } =
        useUpdateBankAccount();

    const routes = Object.values(CreateEditSteps).map(
        (step): { key: CreateEditSteps; title: string } => ({
            key: step,
            title: CREATE_EDIT_TITLE[step] as string,
        }),
    );

    const getRouteIndex = (step: CreateEditSteps) =>
        routes.findIndex(({ key }) => key === step);

    const chooseColorIndex = getRouteIndex(CreateEditSteps.COLOR);

    const onPressNext = (updates?: Partial<UpdateBankAccountInfo>) => {
        if (updates != null) {
            setUpdateAccountInfo({
                ...updateAccountInfo,
                ...updates,
            });
        }
        if (index === chooseColorIndex && spendType === SpendType.SPEND) {
            router.replace('/banking/onboarding/add-card');
        } else {
            setIndex(index + 1);
        }
    };

    const onSelectSpendType = (spendType: SpendType) => {
        setSpendType(spendType);
        onPressNext({
            holisticCategory: convertSpendTypeToHolisticCategory(spendType),
        });
    };

    const navigateBack = () => {
        if (router.canGoBack()) {
            router.back();
        } else {
            router.navigate('/home/banking');
        }
    };

    const onPressBack = () => {
        if (index > 0) {
            setIndex(index - 1);
        } else {
            // Clear out account being edited
            setEditingSpaceAccount(null);
            navigateBack();
        }
    };

    const updateAccountAndRefetchDetails = async ({
        bankAccountId,
        syncteraAccountId,
        nickname,
        icon,
        color,
        holisticCategory,
    }: AccountUpdateDetails) => {
        try {
            await updateAccount({
                urlParams: { bankAccountId },
                body: {
                    syncteraAccountId,
                    nickname,
                    icon,
                    color,
                    holisticCategory,
                },
            });
            setPreselectedAccountFromCard({
                ...(account as AccountDetail),
                nickname,
                icon,
                color,
                holisticCategory,
            });
            void refetchAccountDetails();
        } catch (e) {
            captureException(e);
            router.replace('/home/banking');
            toast.show('Something went wrong updating your account');
            setEditingSpaceAccount(null);
        }
    };

    const onSubmit = async ({
        nickname,
        icon,
        color,
        holisticCategory,
    }: UpdateBankAccountInfo) => {
        if (account == null) return;

        const isMetadataUpdated =
            nickname !== account?.nickname ||
            icon !== account?.icon ||
            color !== account?.color ||
            holisticCategory !== account?.holisticCategory;

        if (
            isMetadataUpdated &&
            nickname != null &&
            icon != null &&
            color != null &&
            holisticCategory != null
        ) {
            await updateAccountAndRefetchDetails({
                bankAccountId: account.bankAccountId,
                syncteraAccountId: account.syncteraAccountId,
                nickname,
                icon,
                color,
                holisticCategory,
            });

            if (spendType === SpendType.SPEND) {
                router.replace('/banking/onboarding/add-card');
            } else {
                router.replace('/home/banking');
            }
            setEditingSpaceAccount(null); // Clear account being edited from global state
        } else {
            router.replace('/banking/onboarding/add-card');
        }
    };

    const renderScene = ({
        route,
    }: SceneRendererProps & {
        route: { key: CreateEditSteps; title: string };
    }) => {
        if (routes[index]?.key !== route.key) {
            return null;
        }

        // Customize step doesn't use ScreenContainer (has custom layout)
        if (route.key === CreateEditSteps.CUSTOMIZE) {
            return <Customize onPressContinue={onPressNext} />;
        }

        // All other steps use ScreenContainer
        return (
            <>
                <ScreenContainer
                    title={route.title}
                    hasBackButton={true}
                    onBack={onPressBack}
                    onFallbackRoute="/home/banking"
                    rightAction={
                        route.key === CreateEditSteps.SPEND ? (
                            <InfoButton
                                onPress={() => {
                                    setIsInfoSheetOpen(true);
                                }}
                            />
                        ) : undefined
                    }
                >
                    {(() => {
                        switch (route.key) {
                            case CreateEditSteps.SPEND:
                                return (
                                    <Spend
                                        jointAccount={
                                            !isEmpty(account?.jointUser)
                                        }
                                        fromKyc={fromKyc === 'true'}
                                        nickname={account?.nickname}
                                        icon={account?.icon}
                                        color={account?.color}
                                        onSelect={onSelectSpendType}
                                    />
                                );
                            case CreateEditSteps.NAME:
                                return (
                                    <Name
                                        onPressNext={onPressNext}
                                        accountInfo={updateAccountInfo}
                                    />
                                );
                            case CreateEditSteps.ICON:
                                return (
                                    <Icon
                                        onPressNext={onPressNext}
                                        accountInfo={updateAccountInfo}
                                    />
                                );
                            case CreateEditSteps.COLOR:
                                return (
                                    <Color
                                        onPressNext={(updates) => {
                                            void onSubmit({
                                                ...updateAccountInfo,
                                                ...updates,
                                            });
                                        }}
                                        accountInfo={updateAccountInfo}
                                    />
                                );
                            default:
                                return <></>;
                        }
                    })()}
                </ScreenContainer>
                {route.key === CreateEditSteps.SPEND && (
                    <SpendInfoSheet
                        isOpen={isInfoSheetOpen}
                        onClose={() => {
                            setIsInfoSheetOpen(false);
                        }}
                    />
                )}
            </>
        );
    };

    if (isUpdateAccountPending) {
        return <FullScreenSpinner />;
    }

    return (
        <>
            <Pressable
                onPress={Keyboard.dismiss}
                disabled={isWeb}
                style={{ flex: 1 }}
            >
                <TabView
                    swipeEnabled={false}
                    navigationState={{ index, routes }}
                    onIndexChange={setIndex}
                    renderScene={renderScene}
                    lazy={({ route }) => route.key === CreateEditSteps.NAME} // TabView's animation causes dismissal of autofocus keyboard in "Name" scene which now opens after delay / animation finish
                    renderTabBar={() => null}
                />
            </Pressable>
        </>
    );
};

export default CustomizeSpace;
